/*
  Régua de preço configurável — Casulo Corporativo
  v2 — 25/09/2026 (revisão da v1 gerada pelo Sidekick em 17/09/2026)

  O que mudou em relação à v1:
  1. A faixa escolhida fica guardada na sessão do navegador e é reaplicada
     automaticamente ao trocar de página da mesma coleção.
  2. Os links de paginação passam a levar a faixa no endereço, então abrir
     a próxima página em nova aba também mantém o filtro.
  3. Os limites da régua são calculados para a coleção inteira (até 50
     produtos, limite da Shopify para seções fora da grade principal),
     e não só para a página atual. A régua não muda de tamanho entre páginas.
  4. Os limites são arredondados para múltiplos do "Incremento" da seção.
     Isso corrige o "Limpar", que deixava os produtos mais caros escondidos.
  5. A régua continua funcionando depois de usar os filtros nativos ou a
     ordenação do Dawn (o vigia agora observa o contêiner que o Dawn não troca).
  6. Ao voltar com o botão "voltar" do navegador, a faixa atual é reaplicada.

  Limitação conhecida (inerente a esta abordagem):
  o filtro esconde cards que já foram carregados. Cada página mostra só os
  seus próprios produtos que estão na faixa; uma página pode ficar vazia
  enquanto a seguinte tem resultados. O contador avisa isso ao cliente.

  Decisões de negócio configuráveis (ver constantes abaixo):
  - KEEP_RANGE_ACROSS_COLLECTIONS: a faixa vale só para a coleção atual
    (false) ou acompanha o cliente em todas as coleções (true).
  - SHOW_PRODUCTS_WITHOUT_PRICE: produtos sem preco_min/preco_max
    aparecem sempre (true) ou somem quando há filtro ativo (false).
*/

(() => {
  'use strict';

  const KEEP_RANGE_ACROSS_COLLECTIONS = false;
  const SHOW_PRODUCTS_WITHOUT_PRICE = true;

  const STORAGE_PREFIX = 'casulo-price-range:v2:';
  const URL_MIN = 'casulo_price_min';
  const URL_MAX = 'casulo_price_max';

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const inputFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const countFormatter = new Intl.NumberFormat('pt-BR');

  /* ---------- Utilitários ---------- */

  const parseLocalizedNumber = (value) => {
    const text = String(value ?? '').trim().replace(/[^0-9,.-]/g, '');
    if (!text) return NaN;

    if (text.includes(',') && text.includes('.')) {
      return Number(text.replace(/\./g, '').replace(',', '.'));
    }

    if (text.includes(',')) {
      return Number(text.replace(',', '.'));
    }

    if (/^\d{1,3}(\.\d{3})+$/.test(text)) {
      return Number(text.replace(/\./g, ''));
    }

    return Number(text);
  };

  const toNumber = (value, fallback = 0) => {
    const parsed = parseLocalizedNumber(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

  const isValidRange = (range) =>
    Boolean(range) && Number.isFinite(range.min) && Number.isFinite(range.max);

  const toUrlValue = (value) => String(Math.round(value * 100) / 100);

  const storage = {
    read(key) {
      try {
        return JSON.parse(sessionStorage.getItem(key) || 'null');
      } catch (error) {
        return null;
      }
    },
    write(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        // Navegador bloqueou a sessão: o filtro funciona, só não é lembrado.
      }
    },
    remove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        // Idem.
      }
    }
  };

  /* ---------- Leitura da grade do Dawn ---------- */

  const getGrid = () => document.getElementById('product-grid');

  // O Dawn substitui o conteúdo deste contêiner ao aplicar filtros nativos,
  // mas o contêiner em si permanece — por isso é ele que observamos.
  const getGridContainer = () => document.getElementById('ProductGridContainer') || getGrid();

  const getProductCards = () => {
    const grid = getGrid();
    return grid ? Array.from(grid.querySelectorAll('li.grid__item')) : [];
  };

  const getPaginationLinks = () => Array.from(document.querySelectorAll('.pagination a[href]'));

  const getCardDataElement = (card) => {
    if (
      card.dataset.casuloProductHandle ||
      card.dataset.casuloPriceMin ||
      card.dataset.casuloPriceMax
    ) {
      return card;
    }

    return card.querySelector(
      '[data-casulo-product-handle], [data-casulo-price-min], [data-casulo-price-max]'
    );
  };

  const getProductHandle = (card) => {
    const dataElement = getCardDataElement(card);
    if (dataElement?.dataset.casuloProductHandle) {
      return dataElement.dataset.casuloProductHandle;
    }

    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;

    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  };

  const getProductRangeFromCard = (card) => {
    const dataElement = getCardDataElement(card);
    if (!dataElement) return null;

    const range = {
      min: parseLocalizedNumber(dataElement.dataset.casuloPriceMin),
      max: parseLocalizedNumber(dataElement.dataset.casuloPriceMax),
      noDiscount: dataElement.dataset.casuloNoDiscount === 'true'
    };

    return isValidRange(range) ? range : null;
  };

  /* ---------- Uma régua ---------- */

  const initRoot = (root) => {
    if (root.dataset.casuloInitialized === 'true') return;
    root.dataset.casuloInitialized = 'true';

    if (root.dataset.loggedInOnly === 'true' && root.dataset.customerLoggedIn !== 'true') {
      root.hidden = true;
      return;
    }

    const el = {
      minInput: root.querySelector('[data-casulo-min-input]'),
      maxInput: root.querySelector('[data-casulo-max-input]'),
      minNumber: root.querySelector('[data-casulo-min-number]'),
      maxNumber: root.querySelector('[data-casulo-max-number]'),
      minLabel: root.querySelector('[data-casulo-min-label]'),
      maxLabel: root.querySelector('[data-casulo-max-label]'),
      activeTrack: root.querySelector('[data-casulo-active-track]'),
      resetButton: root.querySelector('[data-casulo-reset]'),
      applyButton: root.querySelector('[data-casulo-apply]'),
      count: root.querySelector('[data-casulo-count]'),
      productData: root.querySelector('[data-casulo-product-ranges]')
    };

    if (!el.minInput || !el.maxInput || !el.productData) return;

    let productRanges = {};
    try {
      productRanges = JSON.parse(el.productData.textContent || '{}');
    } catch (error) {
      console.error('[Casulo] Não foi possível ler as faixas de preço.', error);
      return;
    }

    const step = Math.max(1, toNumber(root.dataset.sliderStep, 100));

    /* Nível B2B e desconto */
    const marketHandle = root.dataset.marketHandle || '';
    const discountByMarket = {
      [root.dataset.startMarket || 'b2b-1']: toNumber(root.dataset.startDiscount),
      [root.dataset.plusMarket || 'b2b-2']: toNumber(root.dataset.plusDiscount),
      [root.dataset.premiumMarket || 'b2b-3']: toNumber(root.dataset.premiumDiscount)
    };
    const tierDetected = Object.prototype.hasOwnProperty.call(discountByMarket, marketHandle);
    const discount = tierDetected ? discountByMarket[marketHandle] : 0;
    root.dataset.tierDetected = String(tierDetected);

    const getEffectiveRange = (range) => {
      if (!range || range.noDiscount || !discount) return range;
      const factor = 1 - discount;
      return { min: range.min * factor, max: range.max * factor, noDiscount: false };
    };

    const getCardRange = (card) => {
      const fromCard = getProductRangeFromCard(card);
      if (fromCard) return fromCard;
      const handle = getProductHandle(card);
      return handle && productRanges[handle] ? productRanges[handle] : null;
    };

    /* Estado */
    const scope = KEEP_RANGE_ACROSS_COLLECTIONS
      ? 'todas-as-colecoes'
      : root.dataset.collectionHandle || window.location.pathname;
    const selectionKey = `${STORAGE_PREFIX}${scope}:${marketHandle}`;

    let bounds = { min: 0, max: step };
    let selection = null; // null = régua inteira, sem filtro ativo

    const recalculateBounds = () => {
      const cardRanges = getProductCards().map(getProductRangeFromCard).filter(Boolean);
      const ranges = Object.values(productRanges)
        .concat(cardRanges)
        .map(getEffectiveRange)
        .filter(isValidRange);

      let lowest;
      let highest;

      if (ranges.length) {
        lowest = Math.min(...ranges.map((range) => range.min));
        highest = Math.max(...ranges.map((range) => range.max));
      } else {
        lowest = toNumber(root.dataset.sliderMin, 0);
        highest = toNumber(root.dataset.sliderMax, lowest + step);
      }

      const minimum = Math.floor(lowest / step) * step;
      let maximum = Math.ceil(highest / step) * step;
      if (maximum <= minimum) maximum = minimum + step;

      bounds = { min: minimum, max: maximum };

      [el.minInput, el.maxInput].forEach((input) => {
        input.min = minimum;
        input.max = maximum;
        input.step = step;
      });
    };

    // Devolve null quando a faixa cobre a régua inteira (= sem filtro).
    const normalize = (minimum, maximum) => {
      let low = clamp(Number.isFinite(minimum) ? minimum : bounds.min, bounds.min, bounds.max);
      let high = clamp(Number.isFinite(maximum) ? maximum : bounds.max, bounds.min, bounds.max);
      if (low > high) [low, high] = [high, low];
      if (low <= bounds.min && high >= bounds.max) return null;
      return { min: low, max: high };
    };

    const current = () => selection || { min: bounds.min, max: bounds.max };

    /* Desenho da régua */
    const render = () => {
      const { min, max } = current();

      el.minInput.value = min;
      el.maxInput.value = max;
      if (el.minNumber) el.minNumber.value = inputFormatter.format(min);
      if (el.maxNumber) el.maxNumber.value = inputFormatter.format(max);
      if (el.minLabel) el.minLabel.textContent = currencyFormatter.format(min);
      if (el.maxLabel) el.maxLabel.textContent = currencyFormatter.format(max);

      if (el.activeTrack) {
        const total = bounds.max - bounds.min || 1;
        const left = ((min - bounds.min) / total) * 100;
        const right = 100 - ((max - bounds.min) / total) * 100;
        el.activeTrack.style.left = `${clamp(left, 0, 100)}%`;
        el.activeTrack.style.right = `${clamp(right, 0, 100)}%`;
      }
    };

    /* Aplicação do filtro nos cards */
    const filterCards = () => {
      const cards = getProductCards();
      const { min, max } = current();
      let visible = 0;

      cards.forEach((card) => {
        const effective = getEffectiveRange(getCardRange(card));
        let matches = true;

        if (selection) {
          matches = isValidRange(effective)
            ? effective.min <= max && effective.max >= min
            : SHOW_PRODUCTS_WITHOUT_PRICE;
        }

        card.hidden = !matches;
        card.style.display = matches ? '' : 'none';
        if (matches) visible += 1;
      });

      if (!el.count) return;

      if (!selection || !cards.length) {
        el.count.textContent = '';
        return;
      }

      const plural = visible === 1 ? '' : 's';
      const hasPagination = getPaginationLinks().length > 0;

      el.count.textContent = hasPagination
        ? `${countFormatter.format(visible)} produto${plural} desta página na faixa. O filtro continua valendo nas próximas páginas.`
        : `${countFormatter.format(visible)} produto${plural} nesta faixa`;
    };

    /* Endereço da página e links de paginação */
    const writeSelectionToUrl = (url) => {
      if (selection) {
        url.searchParams.set(URL_MIN, toUrlValue(selection.min));
        url.searchParams.set(URL_MAX, toUrlValue(selection.max));
      } else {
        url.searchParams.delete(URL_MIN);
        url.searchParams.delete(URL_MAX);
      }
      return url;
    };

    const syncUrlAndLinks = () => {
      try {
        const pageUrl = writeSelectionToUrl(new URL(window.location.href));
        window.history.replaceState(window.history.state, '', pageUrl);
      } catch (error) {
        // Sem impacto no filtro.
      }

      getPaginationLinks().forEach((link) => {
        try {
          link.href = writeSelectionToUrl(new URL(link.href, window.location.origin)).toString();
        } catch (error) {
          // Link fora do padrão: mantém como está.
        }
      });
    };

    /* Memória da sessão */
    const persist = () => {
      if (selection) storage.write(selectionKey, selection);
      else storage.remove(selectionKey);
    };

    const readStoredSelection = () => {
      const stored = storage.read(selectionKey);
      return isValidRange(stored) ? normalize(stored.min, stored.max) : null;
    };

    const readInitialSelection = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has(URL_MIN) || params.has(URL_MAX)) {
        return normalize(
          toNumber(params.get(URL_MIN), bounds.min),
          toNumber(params.get(URL_MAX), bounds.max)
        );
      }
      return readStoredSelection();
    };

    /* Ciclo de atualização */
    const update = () => {
      render();
      filterCards();
      syncUrlAndLinks();
    };

    const setSelection = (minimum, maximum) => {
      selection = normalize(minimum, maximum);
      persist();
      update();
    };

    const refresh = () => {
      recalculateBounds();
      if (selection) selection = normalize(selection.min, selection.max);
      update();
    };

    /* Interações */
    el.minInput.addEventListener('input', () => {
      const maximum = current().max;
      setSelection(Math.min(toNumber(el.minInput.value, bounds.min), maximum), maximum);
    });

    el.maxInput.addEventListener('input', () => {
      const minimum = current().min;
      setSelection(minimum, Math.max(toNumber(el.maxInput.value, bounds.max), minimum));
    });

    const applyTypedValues = () => {
      setSelection(
        toNumber(el.minNumber?.value, bounds.min),
        toNumber(el.maxNumber?.value, bounds.max)
      );
    };

    if (el.applyButton) el.applyButton.addEventListener('click', applyTypedValues);

    [el.minNumber, el.maxNumber].forEach((field) => {
      if (!field) return;
      field.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          applyTypedValues();
        }
      });
    });

    if (el.resetButton) {
      el.resetButton.addEventListener('click', () => {
        selection = null;
        persist();
        update();
      });
    }

    root.addEventListener('casulo:refresh', refresh);

    /* Filtros nativos / ordenação do Dawn trocam a grade: reaplicar */
    const container = getGridContainer();
    if (container && 'MutationObserver' in window) {
      let timer;
      new MutationObserver((mutations) => {
        if (!mutations.some((mutation) => mutation.type === 'childList')) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(refresh, 100);
      }).observe(container, { childList: true, subtree: true });
    }

    /* Botão "voltar" do navegador pode restaurar a página de memória */
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      recalculateBounds();
      selection = readStoredSelection();
      update();
    });

    /* Início */
    recalculateBounds();
    selection = readInitialSelection();
    persist();
    update();
  };

  const initAll = () => {
    document.querySelectorAll('[data-casulo-price-range]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Editor de temas: seção recarregada ou reordenada.
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);
})();
